import PropTypes from "prop-types";
import { TbLink, TbPencil, TbPlus } from "react-icons/tb";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../ui/CustomCard/CustomCard";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomTextInput from "../../../ui/CustomTextInput/CustomTextInput";
import CustomInlineBanner from "../../../ui/CustomInlineBanner/CustomInlineBanner";
import CallbackUrlModal from "../modals/CallbackUrlModal";
import { notificationStyles } from "../../styles/developerStyles";
import { useCallbackUrl } from "../../hooks/useCallbackUrl";
import { SkeletonCard } from "../DeveloperPageSkeleton";
import FetchFeedback from "../../../ui/CustomFetchFeedback/CustomFetchFeedback";

export default function CallbackUrlCard({ apiEnabled, isDisabling }) {
  const {
    control,
    currentUrl,
    hasCallbackUrl,
    hasCredentials,
    isLoading,
    isFetchError,
    onRetry,
    isSaving,
    modalOpen,
    onOpenModal,
    onModalClose,
    onSubmit,
  } = useCallbackUrl(apiEnabled);

  if (isLoading) return <SkeletonCard bannerHeight={128} />;

  if (isFetchError)
    return (
      <Card className="bg-white border-[#e5e7eb] shadow-none">
        <CardContent>
          <FetchFeedback
            message="Failed to load callback URL settings. Please try again."
            onRetry={onRetry}
            fullHeight={false}
            compact
          />
        </CardContent>
      </Card>
    );

  return (
    <>
      <CallbackUrlModal
        opened={modalOpen}
        onClose={onModalClose}
        control={control}
        onSubmit={onSubmit}
        isSubmitting={isSaving}
        isUpdate={hasCallbackUrl}
      />

      <Card className="bg-white border-[#e5e7eb] shadow-none">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div>
              <CardTitle className="text-base font-semibold">
                Callback URL
              </CardTitle>
              <CardDescription className="mt-1 text-gray-500">
                PayKit sends POST notifications to this URL on C2B transaction
                events
              </CardDescription>
            </div>
            <CustomButton
              leftSection={
                hasCallbackUrl ? <TbPencil size={15} /> : <TbPlus size={15} />
              }
              color="dark"
              radius="md"
              variant="filled"
              className="w-full sm:w-auto self-start sm:shrink-0"
              onClick={onOpenModal}
              disabled={!hasCredentials || isDisabling}
            >
              {hasCallbackUrl ? "Update URL" : "Add URL"}
            </CustomButton>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            {hasCredentials ? (
              <>
                <div>
                  <p className="mb-1.5 text-sm font-medium text-gray-700">
                    Callback URL{" "}
                    <span className="font-normal text-gray-400">
                      (HTTPS required)
                    </span>
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <CustomTextInput
                      value={currentUrl}
                      placeholder="https://your-domain.com/webhook"
                      readOnly
                      disabled={!hasCallbackUrl}
                      radius="md"
                      className="flex-1"
                      leftSection={<TbLink size={16} className="text-gray-400" />}
                      styles={{
                        input: {
                          fontSize: "0.8125rem",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                    <CustomButton
                      variant="default"
                      radius="md"
                      disabled
                      className="w-full sm:w-auto sm:shrink-0"
                    >
                      Test
                    </CustomButton>
                  </div>
                </div>
                <CustomInlineBanner styles={notificationStyles.info}>
                  <p className="text-sm text-[var(--color-primary-600)]">
                    PayKit will retry failed callbacks at least 3 times with
                    exponential backoff. Ensure your endpoint returns a 2xx
                    response to acknowledge receipt.
                  </p>
                </CustomInlineBanner>
              </>
            ) : (
              <CustomInlineBanner styles={notificationStyles.info}>
                <p className="text-sm text-[var(--color-primary-600)]">
                  Generate API credentials first before configuring a callback
                  URL.
                </p>
              </CustomInlineBanner>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

CallbackUrlCard.propTypes = {
  apiEnabled: PropTypes.bool.isRequired,
  isDisabling: PropTypes.bool.isRequired,
};
